import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "../utils/api";
import { Wordmark } from "../components/marketing/Wordmark";
import { SignOut, ArrowsClockwise, EnvelopeOpen, Envelope, CaretDown, DownloadSimple, WhatsappLogo } from "@phosphor-icons/react";

const waLink = (phone, name) => {
  if (!phone) return null;
  let d = String(phone).replace(/[^0-9]/g, "");
  if (!d) return null;
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (d.startsWith("8")) d = "62" + d;
  return `https://wa.me/${d}?text=${encodeURIComponent(`Halo ${name}, terima kasih telah menghubungi PT Darma Daya Guna melalui website kami. `)}`;
};

const mailLink = (s) =>
  `mailto:${s.email}?subject=${encodeURIComponent("Re: Pesan Anda di Website PT Darma Daya Guna")}&body=${encodeURIComponent(`Halo ${s.name},\n\nTerima kasih telah menghubungi PT Darma Daya Guna. Menindaklanjuti pesan Anda mengenai ${s.service || "layanan kami"}:\n\n\n\nSalam,\nTim DDG`)}`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      await axios.get(`${API}/auth/me`);
    } catch {
      try {
        await axios.post(`${API}/auth/refresh`);
      } catch {
        navigate("/admin/login");
        return;
      }
    }
    try {
      const meRes = await axios.get(`${API}/auth/me`);
      setUser(meRes.data);
      const res = await axios.get(`${API}/admin/submissions`);
      setSubmissions(res.data.submissions || []);
    } catch {
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markRead = async (s) => {
    if (s.status === "read") return;
    try {
      await axios.patch(`${API}/admin/submissions/${s.submission_id}`, { status: "read" });
      setSubmissions((prev) =>
        prev.map((x) => (x.submission_id === s.submission_id ? { ...x, status: "read" } : x))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } finally {
      navigate("/admin/login");
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Name", "Company", "Email", "Phone", "Service", "Message", "Status"];
    const rows = submissions.map((s) =>
      [fmtDate(s.created_at), s.name, s.company, s.email, s.phone, s.service, s.message, s.status]
        .map(esc)
        .join(",")
    );
    const csv = "﻿" + [header.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ddg-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const newCount = submissions.filter((s) => s.status === "new").length;
  const weekCount = submissions.filter(
    (s) => Date.now() - new Date(s.created_at).getTime() < 7 * 24 * 3600 * 1000
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper font-plex flex items-center justify-center" data-testid="admin-loading">
        <p className="text-sm text-black/50 tracking-widest uppercase">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-paper font-plex" data-testid="admin-dashboard">
      <header className="border-b border-black/15 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Wordmark size="text-2xl" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-black/40">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="hidden sm:block text-xs text-black/50">
              {user?.email}
            </span>
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              disabled={submissions.length === 0}
              className="flex items-center gap-2 border border-black/20 px-4 py-2.5 text-xs font-semibold hover:bg-brand-green hover:border-brand-green hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <DownloadSimple size={15} weight="bold" />
              Export CSV
            </button>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => { setLoading(true); fetchData(); }}
              className="p-2.5 border border-black/20 hover:bg-black hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
              aria-label="Refresh"
            >
              <ArrowsClockwise size={16} weight="bold" />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-black/20 px-4 py-2.5 text-xs font-semibold hover:bg-brand-red hover:border-brand-red hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              <SignOut size={15} weight="bold" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 border border-black/15 bg-white mb-8" data-testid="admin-stats">
          {[
            { label: "Total Submissions", value: submissions.length, testid: "stat-total" },
            { label: "Unread", value: newCount, testid: "stat-unread", accent: newCount > 0 },
            { label: "Last 7 Days", value: weekCount, testid: "stat-week" },
          ].map((s, i) => (
            <div key={s.label} data-testid={s.testid} className={`p-5 md:p-6 ${i !== 0 ? "border-l border-black/15" : ""}`}>
              <div className={`text-2xl md:text-3xl font-semibold ${s.accent ? "text-brand-red" : "text-black"}`}>
                {s.value}
              </div>
              <div className="mt-1 text-[10px] md:text-xs font-semibold tracking-[0.15em] uppercase text-black/45">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="border border-black/15 bg-white overflow-x-auto" data-testid="submissions-table-wrap">
          <table className="w-full text-sm min-w-[860px]" data-testid="submissions-table">
            <thead>
              <tr className="border-b border-black/15 text-left">
                {["Date", "Name", "Company", "Contact", "Service", "Message", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-black/45">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-black/40 text-sm" data-testid="submissions-empty">
                    No submissions yet. Messages from the contact form will appear here.
                  </td>
                </tr>
              )}
              {submissions.map((s) => (
                <Fragment key={s.submission_id}>
                  <tr
                    data-testid={`submission-row-${s.submission_id}`}
                    className="border-b border-black/10 hover:bg-black/[0.02] cursor-pointer transition-colors"
                    onClick={() => setExpanded(expanded === s.submission_id ? null : s.submission_id)}
                  >
                    <td className="px-4 py-3.5 whitespace-nowrap text-black/60">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3.5 font-medium text-black">{s.name}</td>
                    <td className="px-4 py-3.5 text-black/60">{s.company || "—"}</td>
                    <td className="px-4 py-3.5 text-black/60">
                      <div className="text-xs">{s.email}</div>
                      <div className="text-xs text-black/40">{s.phone}</div>
                    </td>
                    <td className="px-4 py-3.5 text-black/60">{s.service || "—"}</td>
                    <td className="px-4 py-3.5 text-black/60 max-w-[220px]">
                      <span className="line-clamp-1">{s.message}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        data-testid={`submission-status-${s.submission_id}`}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 ${
                          s.status === "new" ? "bg-brand-red/10 text-brand-red" : "bg-brand-green/10 text-brand-green"
                        }`}
                      >
                        {s.status === "new" ? <Envelope size={12} weight="bold" /> : <EnvelopeOpen size={12} weight="bold" />}
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <CaretDown
                        size={14}
                        className={`text-black/40 transition-transform duration-300 ${expanded === s.submission_id ? "rotate-180" : ""}`}
                      />
                    </td>
                  </tr>
                  {expanded === s.submission_id && (
                    <tr className="border-b border-black/10 bg-black/[0.02]">
                      <td colSpan={8} className="px-6 py-5">
                        <p className="text-sm text-black/75 leading-relaxed whitespace-pre-wrap mb-4" data-testid={`submission-message-${s.submission_id}`}>
                          {s.message}
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          {s.status === "new" && (
                            <button
                              data-testid={`mark-read-${s.submission_id}`}
                              onClick={(e) => { e.stopPropagation(); markRead(s); }}
                              className="text-xs font-semibold bg-brand-green text-white px-4 py-2 hover:bg-brand-ink transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
                            >
                              Mark as read
                            </button>
                          )}
                          <a
                            data-testid={`reply-email-${s.submission_id}`}
                            href={mailLink(s)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-xs font-semibold border border-black/25 px-4 py-2 hover:bg-brand-ink hover:text-white hover:border-brand-ink transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
                          >
                            <EnvelopeOpen size={14} weight="bold" />
                            Reply via Email
                          </a>
                          {waLink(s.phone, s.name) && (
                            <a
                              data-testid={`reply-whatsapp-${s.submission_id}`}
                              href={waLink(s.phone, s.name)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-xs font-semibold border border-brand-green text-brand-green px-4 py-2 hover:bg-brand-green hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green"
                            >
                              <WhatsappLogo size={14} weight="bold" />
                              Reply via WhatsApp
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
